<template>
  <div class="report-page">
    <div class="filter-card">
      <div class="fc-row fc-row-primary">
        <div class="field">
          <label class="field-lbl">ช่วงวันที่ (ความเคลื่อนไหว)</label>
          <date-picker
            v-model="dateRange"
            type="date"
            range
            format="DD MMM YY"
            :shortcuts="dateRangeShortcuts"
            :disabled-date="notAfterToday"
            placeholder="เลือกวันที่ต้องการค้นหา"
            input-class="form-control form-control-sm field-input-native"
          ></date-picker>
        </div>

        <b-button variant="primary" class="btn-primary-lg" @click="fetchReport">
          แสดงข้อมูล
        </b-button>
      </div>
    </div>

    <div id="report-elements">
      <div class="toolbar">
        <span></span>
        <div class="toolbar-right">
          <b-dropdown size="sm" variant="outline-success" right :disabled="!movement">
            <template #button-content>
              <i class="fas fa-download"></i>
            </template>
            <b-dropdown-item @click="excelExport">
              <i class="fas fa-file-excel mr-2"></i>Excel
            </b-dropdown-item>
            <b-dropdown-item @click="pdfExport">
              <i class="fas fa-file-pdf mr-2"></i>PDF
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <!-- Block 1: การ์ดสรุป 4 ใบ — four equal-width tiles, independent of the
           movement table below. The same four numbers are also carried into the
           export's "สรุป" sheet (built in buildSummarySheetTable()), so the sheet
           stays self-contained without this markup living inside the table. -->
      <b-row class="deposit-summary-cards mb-3" no-gutters>
        <b-col cols="6" md="3" class="px-1 mb-2">
          <b-card class="summary-tile h-100" body-class="text-center py-2">
            <div class="tile-label">ยอดยกมา</div>
            <div class="tile-num">{{ formatNumber(movementTotals.openingBalance) }}</div>
          </b-card>
        </b-col>
        <b-col cols="6" md="3" class="px-1 mb-2">
          <b-card class="summary-tile h-100" body-class="text-center py-2">
            <div class="tile-label">รับมัดจำ</div>
            <div class="tile-num">{{ formatNumber(movementTotals.deposited) }}</div>
          </b-card>
        </b-col>
        <b-col cols="6" md="3" class="px-1 mb-2">
          <b-card class="summary-tile h-100" body-class="text-center py-2">
            <div class="tile-label">ตัดใช้มัดจำ</div>
            <div class="tile-num">{{ formatNumber(movementTotals.deducted) }}</div>
          </b-card>
        </b-col>
        <b-col cols="6" md="3" class="px-1 mb-2">
          <b-card class="summary-tile h-100" body-class="text-center py-2">
            <div class="tile-label">ยอดยกไป</div>
            <div class="tile-num">{{ formatNumber(movementTotals.closingBalance) }}</div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Block 2: ตารางความเคลื่อนไหวรายสาขา -->
      <b-table
        id="DepositMovementTable"
        ref="DepositMovementTable"
        class="my-0 report-table"
        small
        bordered
        hover
        head-variant="light"
        :items="movementRows"
        :fields="movementFields"
        :responsive="true"
        show-empty
      >
        <template #cell(deposited)="row">{{ formatNumber(row.value) }}</template>
        <template #cell(deducted)="row">{{ formatNumber(row.value) }}</template>
        <template #cell(cancelledDeposit)="row">{{ formatNumber(row.value) }}</template>
        <template #cell(restoredDeduction)="row">{{ formatNumber(row.value) }}</template>
        <template #cell(net)="row">{{ formatNumber(row.value) }}</template>
        <template #cell(patientCount)="row">{{ formatInt(row.value) }}</template>
        <template #custom-foot>
          <b-tr variant="secondary" class="text-bold text-right">
            <td class="text-left">{{ movementTotalRow.branchName }}</td>
            <td>{{ formatNumber(movementTotalRow.deposited) }}</td>
            <td>{{ formatNumber(movementTotalRow.deducted) }}</td>
            <td>{{ formatNumber(movementTotalRow.cancelledDeposit) }}</td>
            <td>{{ formatNumber(movementTotalRow.restoredDeduction) }}</td>
            <td>{{ formatNumber(movementTotalRow.net) }}</td>
            <td>{{ formatInt(movementTotalRow.patientCount) }}</td>
          </b-tr>
        </template>
        <template #empty="">
          <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
        </template>
      </b-table>
      <p class="text-muted small mt-1 mb-4">
        หมายเหตุ: ตัวเลขรายสาขาคือความเคลื่อนไหวเท่านั้น ยอดยกมาและยอดยกไปเป็นของคลินิกรวม
        เพราะกระเป๋าเงินมัดจำผูกกับคนไข้ทั้งคลินิก ไม่ได้แยกตามสาขา
      </p>

      <!-- Block 3: ตารางคงเหลือรายคนไข้ (no pagination — backend returns every row) -->
      <h6 class="section-title">ยอดคงเหลือรายคนไข้</h6>
      <div class="summary-line">
        <span>ยอดคงเหลือรวม: <strong>{{ formatNumber(balanceTotals.balance) }}</strong> บาท</span>
        <span>คนไข้ที่ยังมียอดค้าง: <strong>{{ formatInt(balanceTotals.patientCount) }}</strong> คน</span>
        <span v-if="balance && balance.asOf" class="text-muted">
          ณ วันที่ {{ formatDateTime(balance.asOf) }}
        </span>
      </div>
      <b-table
        id="DepositBalanceTable"
        ref="DepositBalanceTable"
        class="my-0 report-table"
        small
        bordered
        hover
        head-variant="light"
        :items="balanceRows"
        :fields="balanceFields"
        sort-by="balance"
        :sort-desc="true"
        :responsive="true"
        show-empty
      >
        <template #cell(balance)="row">{{ formatNumber(row.value) }}</template>
        <template #cell(aging.m0_3)="row"
          >{{ formatNumber(row.value) }}<span v-if="row.item.agingEstimated">*</span></template
        >
        <template #cell(aging.m3_6)="row"
          >{{ formatNumber(row.value) }}<span v-if="row.item.agingEstimated">*</span></template
        >
        <template #cell(aging.m6_12)="row"
          >{{ formatNumber(row.value) }}<span v-if="row.item.agingEstimated">*</span></template
        >
        <template #cell(aging.m12plus)="row"
          >{{ formatNumber(row.value) }}<span v-if="row.item.agingEstimated">*</span></template
        >
        <template #cell(originBranches)="row">{{ originBranchesText(row.value) }}</template>
        <template #cell(firstDepositDt)="row">{{ formatDate(row.value) }}</template>
        <template #cell(lastMovementDt)="row">{{ formatDate(row.value) }}</template>
        <template #custom-foot>
          <b-tr variant="secondary" class="text-bold text-right">
            <td class="text-left" colspan="2">รวม</td>
            <td>{{ formatNumber(balanceTotals.balance) }}</td>
            <td>{{ formatNumber(balanceTotals.aging.m0_3) }}</td>
            <td>{{ formatNumber(balanceTotals.aging.m3_6) }}</td>
            <td>{{ formatNumber(balanceTotals.aging.m6_12) }}</td>
            <td>{{ formatNumber(balanceTotals.aging.m12plus) }}</td>
            <td></td>
            <td></td>
            <td></td>
          </b-tr>
        </template>
        <template #empty="">
          <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
        </template>
      </b-table>
      <p v-if="hasEstimatedAging" class="text-muted small mt-1 mb-4">
        * อายุของเงินก้อนนี้เป็นค่าประมาณ
      </p>

      <!-- Block 4: ตารางตัดใช้ข้ามสาขา — hidden entirely when there is none -->
      <template v-if="crossBranchUsage.length > 0">
        <h6 class="section-title">ตัดใช้ข้ามสาขา</h6>
        <b-table
          id="DepositCrossBranchTable"
          ref="DepositCrossBranchTable"
          class="my-0 report-table mb-4"
          small
          hover
          head-variant="light"
          :items="crossBranchUsage"
          :fields="crossBranchFields"
          :responsive="true"
          show-empty
        >
          <template #cell(amount)="row">{{ formatNumber(row.value) }}</template>
          <template #cell(patientCount)="row">{{ formatInt(row.value) }}</template>
        </b-table>
      </template>

      <!-- Block 5: ตารางรายการ ledger — server-side pagination via count/offset/limit -->
      <div class="toolbar">
        <h6 class="section-title mb-0">รายการ</h6>
        <div class="toolbar-right">
          <b-pagination
            v-model="linesPage"
            :total-rows="linesCount"
            :per-page="linesPerPage"
            size="sm"
            first-number
            last-number
            class="mb-0"
            @change="loadLinesPage"
          ></b-pagination>
        </div>
      </div>
      <b-table
        id="DepositLinesTable"
        ref="DepositLinesTable"
        class="my-0 report-table"
        small
        hover
        head-variant="light"
        :busy="linesBusy"
        :items="linesRows"
        :fields="linesFields"
        :responsive="true"
        show-empty
      >
        <template #cell(creationDt)="row">{{ formatDateTime(row.value) }}</template>
        <template #cell(type)="row">
          <b-badge :variant="typeVariant(row.value)">{{ typeLabel(row.value) }}</b-badge>
        </template>
        <template #cell(amount)="row">{{ formatNumber(row.value) }}</template>
        <template #cell(balanceAfter)="row">{{ formatNumber(row.value) }}</template>
        <template #empty="">
          <p class="text-center text-muted my-2">ไม่มีข้อมูลให้แสดง</p>
        </template>
      </b-table>
    </div>

    <Dialog ref="Dialog"></Dialog>
    <Loading v-if="isLoading"></Loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";

import Dialog from "@/components/modal/Dialog";
import Loading from "@/components/Loading";
import { exportTableToExcel, exportReportToPdf } from "@/utils/reportExport";

// One map used everywhere a transaction type is shown on screen or exported.
const TYPE_LABEL = {
  DEPOSIT: "รับมัดจำ",
  DEDUCTION: "ตัดใช้มัดจำ",
  CANCEL_DEPOSIT: "ยกเลิกรับมัดจำ",
  CANCEL_DEDUCTION: "คืนจากยกเลิก",
  ADJUSTMENT: "ปรับยอด",
};

const TYPE_VARIANT = {
  DEPOSIT: "success",
  DEDUCTION: "danger",
  CANCEL_DEPOSIT: "warning",
  CANCEL_DEDUCTION: "info",
  ADJUSTMENT: "secondary",
};

const EMPTY_AGING = { m0_3: 0, m3_6: 0, m6_12: 0, m12plus: 0 };

export default {
  name: "ClinicDepositReport",
  components: {
    Dialog,
    Loading,
  },
  data() {
    return {
      isLoading: false,
      reportTitle: "รายงานเงินมัดจำ (คลินิก)",
      reportNameEn: "DepositReport",

      dateRange: [],

      // Raw API responses — kept whole so totals/asOf/etc. stay easy to reach.
      movement: null,
      balance: null,

      linesRows: [],
      linesCount: 0,
      linesPage: 1,
      linesPerPage: 50,
      linesBusy: false,

      movementFields: [
        { key: "branchName", label: "สาขา", class: "narrow-spacing" },
        { key: "deposited", label: "รับมัดจำ", class: "text-right" },
        { key: "deducted", label: "ตัดใช้มัดจำ", class: "text-right" },
        { key: "cancelledDeposit", label: "ยกเลิกรับ", class: "text-right" },
        { key: "restoredDeduction", label: "คืนจากยกเลิก", class: "text-right" },
        { key: "net", label: "สุทธิ", class: "text-right" },
        { key: "patientCount", label: "คนไข้ที่มีความเคลื่อนไหว", class: "text-right" },
      ],
      balanceFields: [
        { key: "patientName", label: "คนไข้", class: "narrow-spacing" },
        { key: "dn", label: "HN", class: "narrow-spacing" },
        { key: "balance", label: "คงเหลือ", class: "text-right" },
        { key: "aging.m0_3", label: "0–3 ด.", class: "text-right" },
        { key: "aging.m3_6", label: "3–6 ด.", class: "text-right" },
        { key: "aging.m6_12", label: "6–12 ด.", class: "text-right" },
        { key: "aging.m12plus", label: ">12 ด.", class: "text-right" },
        { key: "originBranches", label: "สาขาที่รับเงิน", class: "narrow-spacing" },
        { key: "firstDepositDt", label: "รับครั้งแรก", class: "narrow-spacing" },
        { key: "lastMovementDt", label: "เคลื่อนไหวล่าสุด", class: "narrow-spacing" },
      ],
      crossBranchFields: [
        { key: "fromBranchName", label: "สาขาที่รับเงิน", class: "narrow-spacing" },
        { key: "toBranchName", label: "สาขาที่ตัดใช้", class: "narrow-spacing" },
        { key: "amount", label: "จำนวนเงิน", class: "text-right" },
        { key: "patientCount", label: "จำนวนคนไข้", class: "text-right" },
      ],
      linesFields: [
        { key: "creationDt", label: "วันที่", class: "narrow-spacing" },
        { key: "branchName", label: "สาขา", class: "narrow-spacing" },
        { key: "patientName", label: "คนไข้", class: "narrow-spacing" },
        { key: "dn", label: "HN", class: "narrow-spacing" },
        { key: "type", label: "ประเภท", class: "text-center" },
        { key: "receiptNo", label: "เลขที่ใบเสร็จ", class: "narrow-spacing" },
        { key: "amount", label: "จำนวน", class: "text-right" },
        { key: "balanceAfter", label: "คงเหลือหลังรายการ", class: "text-right" },
        { key: "note", label: "หมายเหตุ", class: "narrow-spacing" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      getReportDateRange: "moduleReport/getReportDateRange",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      clinicInfo: "moduleBranch/getClinicInfo",
    }),
    movementTotals() {
      return (
        (this.movement && this.movement.totals) || {
          openingBalance: 0,
          deposited: 0,
          deducted: 0,
          cancelledDeposit: 0,
          restoredDeduction: 0,
          closingBalance: 0,
          patientCount: 0,
          txnCount: 0,
        }
      );
    },
    movementRows() {
      return (this.movement && this.movement.branches) || [];
    },
    movementTotalRow() {
      const rows = this.movementRows;
      const sum = key => rows.reduce((s, r) => s + (Number(r[key]) || 0), 0);
      return {
        branchName: "รวมทุกสาขา",
        deposited: sum("deposited"),
        deducted: sum("deducted"),
        cancelledDeposit: sum("cancelledDeposit"),
        restoredDeduction: sum("restoredDeduction"),
        net: sum("net"),
        patientCount: this.movementTotals.patientCount,
      };
    },
    crossBranchUsage() {
      return (this.movement && this.movement.crossBranchUsage) || [];
    },
    balanceRows() {
      return (this.balance && this.balance.rows) || [];
    },
    balanceTotals() {
      const totals = (this.balance && this.balance.totals) || {
        balance: 0,
        patientCount: 0,
        aging: EMPTY_AGING,
      };
      return { ...totals, aging: totals.aging || EMPTY_AGING };
    },
    hasEstimatedAging() {
      return this.balanceRows.some(r => r.agingEstimated);
    },
    today() {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return today;
    },
  },
  methods: {
    ...mapActions({
      fetchDepositMovementReport: "moduleReport/fetchDepositMovementReport",
      fetchDepositBalanceReport: "moduleReport/fetchDepositBalanceReport",
      fetchDepositLinesReport: "moduleReport/fetchDepositLinesReport",
      setStateReportDateRange: "moduleReport/setStateReportDateRange",
    }),
    formatNumber(value) {
      if (value === null || value === undefined || isNaN(value)) return "";
      return Number(value).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    formatInt(value) {
      if (value === null || value === undefined || isNaN(value)) return "0";
      return Number(value).toLocaleString("th-TH");
    },
    formatDate(date) {
      return date ? moment(date).locale("th").format("D MMM YY") : "";
    },
    formatDateTime(date) {
      return date ? moment(date).locale("th").format("D MMM YY HH:mm") : "";
    },
    typeLabel(type) {
      return TYPE_LABEL[type] || type;
    },
    typeVariant(type) {
      return TYPE_VARIANT[type] || "secondary";
    },
    originBranchesText(list) {
      if (!Array.isArray(list) || !list.length) return "";
      return list
        .map(b => `${b.branchName || "ไม่ระบุสาขา"} (${this.formatNumber(b.amount)})`)
        .join(", ");
    },
    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
    rangeParams() {
      return {
        clinicUrl: this.$route.params.clinicUrl,
        startDt: moment(this.dateRange[0]).startOf("day").toISOString(),
        // endDt is exclusive: the day after dateTo at 00:00
        endDt: moment(this.dateRange[1]).add(1, "day").startOf("day").toISOString(),
      };
    },
    async fetchReport() {
      if (!this.dateRange[0] || !this.dateRange[1]) return;
      this.setStateReportDateRange(this.dateRange);

      this.isLoading = true;
      this.linesPage = 1;
      const params = this.rangeParams();
      try {
        const [movementRes, balanceRes, linesRes] = await Promise.all([
          this.fetchDepositMovementReport(params),
          this.fetchDepositBalanceReport({ clinicUrl: params.clinicUrl }),
          this.fetchDepositLinesReport({ ...params, offset: 0, limit: this.linesPerPage }),
        ]);
        this.movement = movementRes.data;
        this.balance = balanceRes.data;
        this.linesRows = linesRes.data.rows || [];
        this.linesCount = linesRes.data.count || 0;
      } catch (err) {
        console.error(err);
        this.$refs.Dialog.showToast("danger", "ไม่สามารถโหลดรายงานเงินมัดจำได้");
      } finally {
        this.isLoading = false;
      }
    },
    async loadLinesPage(page) {
      this.linesPage = page;
      this.linesBusy = true;
      try {
        const params = this.rangeParams();
        const offset = (page - 1) * this.linesPerPage;
        const res = await this.fetchDepositLinesReport({
          ...params,
          offset,
          limit: this.linesPerPage,
        });
        this.linesRows = res.data.rows || [];
        this.linesCount = res.data.count || 0;
      } catch (err) {
        console.error(err);
        this.$refs.Dialog.showToast("danger", "ไม่สามารถโหลดรายการเงินมัดจำได้");
      } finally {
        this.linesBusy = false;
      }
    },
    // Builds a detached <table> (never attached to the page) carrying the 4
    // headline totals plus the per-branch breakdown, so the "สรุป" export sheet
    // still gets both even though block 1 is now its own card row on screen,
    // separate from the movement table's markup.
    buildSummarySheetTable() {
      const table = document.createElement("table");

      const thead = document.createElement("thead");
      const headRow = document.createElement("tr");
      this.movementFields.forEach(f => {
        const th = document.createElement("th");
        th.textContent = f.label;
        headRow.appendChild(th);
      });
      thead.appendChild(headRow);
      table.appendChild(thead);

      const tbody = document.createElement("tbody");

      const totalsCells = [
        `ยอดยกมา: ${this.formatNumber(this.movementTotals.openingBalance)}`,
        `รับมัดจำ: ${this.formatNumber(this.movementTotals.deposited)}`,
        `ตัดใช้มัดจำ: ${this.formatNumber(this.movementTotals.deducted)}`,
        `ยอดยกไป: ${this.formatNumber(this.movementTotals.closingBalance)}`,
      ];
      const totalsRow = document.createElement("tr");
      this.movementFields.forEach((f, idx) => {
        const td = document.createElement("td");
        td.textContent = totalsCells[idx] || "";
        totalsRow.appendChild(td);
      });
      tbody.appendChild(totalsRow);

      const spacerRow = document.createElement("tr");
      this.movementFields.forEach(() => spacerRow.appendChild(document.createElement("td")));
      tbody.appendChild(spacerRow);

      this.movementRows.forEach(row => {
        const tr = document.createElement("tr");
        this.movementFields.forEach(f => {
          const td = document.createElement("td");
          if (f.class && f.class.includes("text-right")) td.classList.add("text-right");
          let value = row[f.key];
          if (f.key === "patientCount") value = this.formatInt(value);
          else if (f.key !== "branchName") value = this.formatNumber(value);
          td.textContent = value == null ? "" : value;
          tr.appendChild(td);
        });
        tbody.appendChild(tr);
      });
      table.appendChild(tbody);

      const tfoot = document.createElement("tfoot");
      const footRow = document.createElement("tr");
      const footValues = [
        this.movementTotalRow.branchName,
        this.formatNumber(this.movementTotalRow.deposited),
        this.formatNumber(this.movementTotalRow.deducted),
        this.formatNumber(this.movementTotalRow.cancelledDeposit),
        this.formatNumber(this.movementTotalRow.restoredDeduction),
        this.formatNumber(this.movementTotalRow.net),
        this.formatInt(this.movementTotalRow.patientCount),
      ];
      this.movementFields.forEach((f, idx) => {
        const td = document.createElement("td");
        if (f.class && f.class.includes("text-right")) td.classList.add("text-right");
        td.textContent = footValues[idx];
        footRow.appendChild(td);
      });
      tfoot.appendChild(footRow);
      table.appendChild(tfoot);

      return table;
    },
    exportOptions() {
      const sheets = [
        { tableEl: this.buildSummarySheetTable(), name: "สรุป" },
        { tableId: "DepositBalanceTable", name: "คงเหลือ" },
      ];
      if (this.crossBranchUsage.length > 0) {
        sheets.push({ tableId: "DepositCrossBranchTable", name: "ข้ามสาขา" });
      }
      sheets.push({ tableId: "DepositLinesTable", name: "รายการ" });

      return {
        sheets,
        reportTitle: this.reportTitle,
        reportNameEn: this.reportNameEn,
        dateFrom: this.dateRange[0],
        dateTo: this.dateRange[1],
        clinicName: this.clinicInfo && this.clinicInfo.name,
      };
    },
    // The ledger table only ever holds one page of rows (loadLinesPage replaces
    // them on every page change). Exporting the DOM table as-is would silently
    // truncate the "รายการ" sheet to whatever page happened to be on screen, with
    // no indication anything was cut — wrong for a financial reconciliation
    // report. So export always fetches the FULL range first, waits for the table
    // to re-render, runs the export, then restores whatever page the user was on.
    async withFullLedgerForExport(action) {
      if (!this.movement) return;

      const previousPage = this.linesPage;
      this.isLoading = true;
      try {
        const params = this.rangeParams();
        const fullLimit = this.linesCount > 0 ? this.linesCount : 100000;
        const res = await this.fetchDepositLinesReport({
          ...params,
          offset: 0,
          limit: fullLimit,
        });
        this.linesRows = res.data.rows || [];
        await this.$nextTick();
        action();
      } catch (err) {
        console.error(err);
        this.$refs.Dialog.showToast("danger", "ไม่สามารถเตรียมข้อมูลสำหรับ export ได้");
      } finally {
        await this.loadLinesPage(previousPage);
        this.isLoading = false;
      }
    },
    excelExport() {
      this.withFullLedgerForExport(() => {
        exportTableToExcel(this.exportOptions());
      });
    },
    pdfExport() {
      this.withFullLedgerForExport(() => {
        exportReportToPdf({ ...this.exportOptions(), autoFit: true });
      });
    },
  },
  mounted() {
    if (this.getReportDateRange[0] && this.getReportDateRange[1]) {
      this.dateRange = this.getReportDateRange;
    } else {
      this.dateRange = [this.today, this.today];
    }
  },
};
</script>

<style scoped>
.section-title {
  margin: 0 0 8px 0;
  font-weight: 600;
}
.summary-line {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 8px;
  font-size: 0.9rem;
}
.summary-tile {
  background: #eaf3ff;
  border-color: #cfe2ff;
}
.summary-tile .tile-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  color: #495057;
  font-weight: 600;
}
.summary-tile .tile-num {
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2d3d;
  margin-top: 2px;
}
</style>
